/**
 * Canonical analytics taxonomy for Discover.
 *
 * Every event name follows the shape: `Discover | <Area> | <Action>`.
 * Call sites should reference the `EVENTS` constants (or `buildEventName` /
 * `useTrackEvent`) rather than hand-writing strings, so the taxonomy stays
 * consistent across the app and groups cleanly in Mixpanel.
 *
 * See `docs/analytics-tracking-plan.md` for the full catalog and rationale.
 */
export const ANALYTICS_PRODUCT = "Discover";
export var AnalyticsArea;
(function (AnalyticsArea) {
    AnalyticsArea["Auth"] = "Auth";
    AnalyticsArea["Landing"] = "Landing";
    AnalyticsArea["Demo"] = "Demo";
    AnalyticsArea["Onboarding"] = "Onboarding";
    AnalyticsArea["Home"] = "Home";
    AnalyticsArea["NewReport"] = "New Report";
    AnalyticsArea["Report"] = "Report";
    AnalyticsArea["Results"] = "Results";
    AnalyticsArea["Overview"] = "Overview";
    AnalyticsArea["Community"] = "Community";
    AnalyticsArea["Recommendations"] = "Recommendations";
    AnalyticsArea["Subtopics"] = "Subtopics";
    AnalyticsArea["ReportSettings"] = "Report Settings";
    AnalyticsArea["Billing"] = "Billing";
    AnalyticsArea["Checkout"] = "Checkout";
    AnalyticsArea["Admin"] = "Admin";
    AnalyticsArea["Error"] = "Error";
})(AnalyticsArea || (AnalyticsArea = {}));
/** Builds a taxonomy-compliant event name: `Discover | <Area> | <Action>`. */
export const buildEventName = (area, action) => `${ANALYTICS_PRODUCT} | ${area} | ${action}`;
/**
 * Canonical event-name constants for the net-new events introduced by
 * DISCOVER-739 (items 2-17 of the tracking plan). Page views (item 1) are
 * handled centrally by `usePageTracking` and have no constant here.
 */
export const EVENTS = {
    auth: {
        loginSucceeded: buildEventName(AnalyticsArea.Auth, "Login Succeeded"),
        loginFailed: buildEventName(AnalyticsArea.Auth, "Login Failed"),
        loggedOut: buildEventName(AnalyticsArea.Auth, "Logged Out"),
        invalidTokenHit: buildEventName(AnalyticsArea.Auth, "Invalid Token Hit"),
    },
    newReport: {
        started: buildEventName(AnalyticsArea.NewReport, "Started"),
        communitySearched: buildEventName(AnalyticsArea.NewReport, "Community Searched"),
        communitySelected: buildEventName(AnalyticsArea.NewReport, "Community Selected"),
        submitted: buildEventName(AnalyticsArea.NewReport, "Submitted"),
    },
    report: {
        generationStarted: buildEventName(AnalyticsArea.Report, "Generation Started"),
        generationCompleted: buildEventName(AnalyticsArea.Report, "Generation Completed"),
        generationFailed: buildEventName(AnalyticsArea.Report, "Generation Failed"),
    },
    community: {
        viewed: buildEventName(AnalyticsArea.Community, "Viewed"),
    },
    overview: {
        viewed: buildEventName(AnalyticsArea.Overview, "Viewed"),
    },
    admin: {
        dashboardViewed: buildEventName(AnalyticsArea.Admin, "Dashboard Viewed"),
    },
    error: {
        apiRequestFailed: buildEventName(AnalyticsArea.Error, "API Request Failed"),
        pageNotFound: buildEventName(AnalyticsArea.Error, "Page Not Found"),
    },
};
